import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Text,
  Button,
  HStack,
  VStack,
  Skeleton,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  EditIcon,
  InfoIcon,
} from '@chakra-ui/icons';

import type { AvailableCarrier, CarrierQuote } from 'api/quote-marketplace';
import type { QuoteMarketplaceTheme } from '../../hooks';
import { formatCurrency } from '../../constants';
import {
  CarrierCell,
  STICKY_LABEL_WIDTH,
  STICKY_REQUESTED_WIDTH,
} from './CarrierCell';
import { calculateQuotePremiumBreakdown } from '../../utils/premiumCalculations';

const PreliminaryPremiumInfo = () => (
  <Tooltip
    label="Preliminary premium from the initial quote. This becomes the final premium once all coverage details finish loading."
    hasArrow
    placement="top"
    maxW="260px"
  >
    <span>
      <Icon
        as={InfoIcon}
        boxSize={3}
        color="orange.400"
        cursor="help"
        verticalAlign="middle"
        mb="2px"
      />
    </span>
  </Tooltip>
);

interface TableWrapperProps {
  theme: QuoteMarketplaceTheme;
  children: React.ReactNode;
}

export const TableWrapper = ({ theme, children }: TableWrapperProps) => (
  <Box
    overflowX="auto"
    bg={theme.bgColor}
    borderWidth="1px"
    borderColor={theme.borderColor}
    borderRadius="lg"
  >
    <Table size="sm" variant="simple">
      {children}
    </Table>
  </Box>
);

interface StickyLabelCellProps {
  bg: string;
  borderColor: string;
  isBold?: boolean;
  minW?: string;
  children: React.ReactNode;
  onClick?: () => void;
  whiteSpace?: 'nowrap' | 'normal';
}

export const StickyLabelCell = ({
  bg,
  borderColor,
  isBold = false,
  minW = `${STICKY_LABEL_WIDTH}px`,
  children,
  onClick,
  whiteSpace,
}: StickyLabelCellProps) => (
  <Td
    position="sticky"
    left={0}
    bg={bg}
    borderColor={borderColor}
    py={3}
    fontWeight={isBold ? '600' : '500'}
    zIndex={2}
    minW={minW}
    whiteSpace={whiteSpace}
    onClick={onClick}
    cursor={onClick ? 'pointer' : undefined}
  >
    {children}
  </Td>
);

interface RequestedCellProps {
  bg: string;
  borderColor: string;
  onEdit?: () => void;
  isEditDisabled?: boolean;
}

export const RequestedCell = ({
  bg,
  borderColor,
  onEdit,
  isEditDisabled,
}: RequestedCellProps) => (
  <Td
    bg={bg}
    borderColor={borderColor}
    py={3}
    textAlign="center"
    position="sticky"
    left={`${STICKY_LABEL_WIDTH}px`}
    zIndex={2}
    minW={`${STICKY_REQUESTED_WIDTH}px`}
  >
    <VStack spacing={1}>
      <Text
        fontSize="xs"
        fontWeight="600"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        Requested
      </Text>
      {onEdit && (
        <Button
          size="xs"
          variant="outline"
          colorScheme="blue"
          leftIcon={<EditIcon boxSize={3} />}
          onClick={onEdit}
          isDisabled={isEditDisabled}
        >
          Edit
        </Button>
      )}
    </VStack>
  </Td>
);

interface ItemHeaderRowProps {
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit?: () => void;
  isEditDisabled?: boolean;
  availableCarriers: AvailableCarrier[];
  getQuoteForCarrier: (carrier: AvailableCarrier) => CarrierQuote | undefined;
  isCarrierSelected: (carrier: AvailableCarrier) => boolean;
  isLoading: boolean;
  theme: QuoteMarketplaceTheme;
  getPremiumForQuote?: (quote: CarrierQuote) => string | null;
  premiumSubLabel?: string;
}

export const ItemHeaderRow = ({
  label,
  isExpanded,
  onToggle,
  onEdit,
  isEditDisabled,
  availableCarriers,
  getQuoteForCarrier,
  isCarrierSelected,
  isLoading,
  theme,
  getPremiumForQuote,
  premiumSubLabel,
}: ItemHeaderRowProps) => (
  <Tr cursor="pointer" bg={theme.headerBg} _hover={{ bg: theme.hoverBg }}>
    <StickyLabelCell
      bg={theme.headerBg}
      borderColor={theme.borderColor}
      isBold
      whiteSpace="nowrap"
      onClick={onToggle}
    >
      <HStack spacing={2}>
        <Icon
          as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
          boxSize={5}
          color="blue.500"
        />
        <Text color="blue.600">{label}</Text>
      </HStack>
    </StickyLabelCell>
    <RequestedCell
      bg={theme.headerBg}
      borderColor={theme.borderColor}
      onEdit={onEdit}
      isEditDisabled={isEditDisabled}
    />
    {availableCarriers.map((carrier, index) => {
      const isSelected = isCarrierSelected(carrier);
      const quote = getQuoteForCarrier(carrier);
      const premium = quote
        ? getPremiumForQuote
          ? getPremiumForQuote(quote)
          : quote.annual_premium
        : null;
      return (
        <CarrierCell
          key={carrier.carrier_id}
          isSelected={isSelected}
          theme={theme}
          fontWeight="600"
          isSticky={index === 0 && isSelected}
        >
          {(!quote || quote.status === 'pending') && isLoading ? (
            <Skeleton height="20px" width="70px" mx="auto" />
          ) : quote?.status === 'quoted' ? (
            <VStack spacing={0}>
              <Text fontSize="md" fontWeight="600">
                {formatCurrency(premium)}
              </Text>
              {premiumSubLabel && premium && (
                <Text fontSize="xs" fontWeight="400" color="gray.500">
                  {premiumSubLabel}
                </Text>
              )}
            </VStack>
          ) : (
            '-'
          )}
        </CarrierCell>
      );
    })}
  </Tr>
);

interface PremiumRowProps {
  label: string;
  getValue: (quote: CarrierQuote | undefined) => string;
  isBold?: boolean;
  availableCarriers: AvailableCarrier[];
  getQuoteForCarrier: (carrier: AvailableCarrier) => CarrierQuote | undefined;
  isCarrierSelected: (carrier: AvailableCarrier) => boolean;
  isLoading: boolean;
  theme: QuoteMarketplaceTheme;
}

export const PremiumRow = ({
  label,
  getValue,
  isBold = false,
  availableCarriers,
  getQuoteForCarrier,
  isCarrierSelected,
  isLoading,
  theme,
}: PremiumRowProps) => (
  <Tr bg={isBold ? theme.headerBg : undefined}>
    <StickyLabelCell
      bg={isBold ? theme.headerBg : theme.bgColor}
      borderColor={theme.borderColor}
      isBold={isBold}
    >
      <Text fontSize="sm" textTransform={isBold ? 'uppercase' : undefined}>
        {label}
      </Text>
    </StickyLabelCell>
    <Td
      bg={theme.requestedBg}
      borderColor={theme.borderColor}
      py={3}
      textAlign="center"
      position="sticky"
      left={`${STICKY_LABEL_WIDTH}px`}
      zIndex={2}
      minW={`${STICKY_REQUESTED_WIDTH}px`}
    />
    {availableCarriers.map((carrier, index) => {
      const quote = getQuoteForCarrier(carrier);
      const isSelected = isCarrierSelected(carrier);
      return (
        <CarrierCell
          key={carrier.carrier_id}
          isSelected={isSelected}
          theme={theme}
          fontWeight="600"
          fontSize="md"
          isSticky={index === 0 && isSelected}
        >
          {(!quote ||
            (quote.status === 'pending' && quote.annual_premium == null)) &&
          isLoading ? (
            <Skeleton height="20px" width="70px" mx="auto" />
          ) : quote?.status === 'pending' && quote.annual_premium != null ? (
            <HStack spacing={1} justify="center">
              <Text as="span">{getValue(quote)}</Text>
              <PreliminaryPremiumInfo />
            </HStack>
          ) : (
            getValue(quote)
          )}
        </CarrierCell>
      );
    })}
  </Tr>
);

interface ItemSeparatorRowProps {
  colSpan: number;
  borderColor: string;
}

export const ItemSeparatorRow = ({
  colSpan,
  borderColor,
}: ItemSeparatorRowProps) => (
  <Tr>
    <Td colSpan={colSpan} p={0} borderColor={borderColor} />
  </Tr>
);

interface MonthlyPremiumSectionProps {
  availableCarriers: AvailableCarrier[];
  getQuoteForCarrier: (carrier: AvailableCarrier) => CarrierQuote | undefined;
  isCarrierSelected: (carrier: AvailableCarrier) => boolean;
  isLoading: boolean;
  theme: QuoteMarketplaceTheme;
}

export const MonthlyPremiumSection = ({
  availableCarriers,
  getQuoteForCarrier,
  isCarrierSelected,
  isLoading,
  theme,
}: MonthlyPremiumSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <Tr
        cursor="pointer"
        bg={theme.headerBg}
        _hover={{ bg: theme.hoverBg }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <StickyLabelCell
          bg={theme.headerBg}
          borderColor={theme.borderColor}
          isBold
        >
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="sm" textTransform="uppercase">
              Estimated Monthly Total
            </Text>
            <HStack spacing={1}>
              <Text fontSize="xs" color="gray.500">
                Total Estimated Cost Summary
              </Text>
              <Icon
                as={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                boxSize={3}
                color="blue.500"
              />
            </HStack>
          </VStack>
        </StickyLabelCell>
        <Td
          bg={theme.requestedBg}
          borderColor={theme.borderColor}
          py={3}
          textAlign="center"
          position="sticky"
          left={`${STICKY_LABEL_WIDTH}px`}
          zIndex={2}
          minW={`${STICKY_REQUESTED_WIDTH}px`}
        />
        {availableCarriers.map((carrier, index) => {
          const quote = getQuoteForCarrier(carrier);
          const isSelected = isCarrierSelected(carrier);
          return (
            <CarrierCell
              key={carrier.carrier_id}
              isSelected={isSelected}
              theme={theme}
              fontWeight="600"
              fontSize="md"
              isSticky={index === 0 && isSelected}
            >
              {(!quote ||
                (quote.status === 'pending' &&
                  quote.monthly_premium == null)) &&
              isLoading ? (
                <Skeleton height="20px" width="70px" mx="auto" />
              ) : quote?.status === 'pending' &&
                quote.monthly_premium != null ? (
                <HStack spacing={1} justify="center">
                  <Text as="span">{formatCurrency(quote.monthly_premium)}</Text>
                  <PreliminaryPremiumInfo />
                </HStack>
              ) : quote?.status === 'quoted' &&
                quote?.monthly_premium != null ? (
                formatCurrency(quote.monthly_premium)
              ) : (
                '-'
              )}
            </CarrierCell>
          );
        })}
      </Tr>

      {isExpanded && (
        <Tr>
          <StickyLabelCell bg={theme.bgColor} borderColor={theme.borderColor}>
            <VStack align="flex-start" spacing={1} pl={7}>
              <Text fontSize="xs" color="gray.500">
                Tax
              </Text>
              <Text fontSize="xs" fontWeight="600">
                Total Est. Cost (Monthly)
              </Text>
            </VStack>
          </StickyLabelCell>
          <Td
            bg={theme.requestedBg}
            borderColor={theme.borderColor}
            py={3}
            textAlign="center"
            position="sticky"
            left={`${STICKY_LABEL_WIDTH}px`}
            zIndex={2}
            minW={`${STICKY_REQUESTED_WIDTH}px`}
          />
          {availableCarriers.map((carrier, index) => {
            const quote = getQuoteForCarrier(carrier);
            const isSelected = isCarrierSelected(carrier);
            const { monthlyPremium, monthlyTax } =
              calculateQuotePremiumBreakdown(quote);
            const tax =
              quote?.status === 'quoted'
                ? formatCurrency(String(monthlyTax.toFixed(2)))
                : '-';
            const total =
              quote?.status === 'quoted'
                ? formatCurrency(String(monthlyPremium.toFixed(2)))
                : '-';

            return (
              <CarrierCell
                key={carrier.carrier_id}
                isSelected={isSelected}
                theme={theme}
                isSticky={index === 0 && isSelected}
              >
                {(!quote || quote.status === 'pending') && isLoading ? (
                  <Skeleton height="30px" width="70px" mx="auto" />
                ) : (
                  <VStack spacing={1}>
                    <Text fontSize="xs" color="gray.500">
                      {tax}
                    </Text>
                    <Text fontSize="sm" fontWeight="600">
                      {total}
                    </Text>
                  </VStack>
                )}
              </CarrierCell>
            );
          })}
        </Tr>
      )}
    </>
  );
};

export { Thead, Tbody };
